import {
	PageWrapperStyled,
	StyledControlsWrapper,
	ChartCard,
} from './EditSensor.styles';
import {
	InfoCard,
	WarningDialog,
	error,
	useIsAdmin,
	Resolutions,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import {
	deleteConfiguration,
	getConfigurationDetails,
	peekSensorReset,
	pollSensorTroubleshootingStop,
	restoreConfigurationByIdStatus,
	restoreStatusStore,
	selectConfiguration,
} from '@Actions';
import { mapDataToForm, stringifyLocationCoords } from './helpers';
import {
	SensorFormChanges,
	useConfigurationSensorDetail,
	useMapLocationState,
	useNavigateTo,
	useSensorFormState,
	useSensorStatusChecker,
	useSensorTroubleshoot,
	useSensorTroubleshootMessages,
} from '@Hooks';
import { useDispatch, useSelector } from 'react-redux';

import ControlsButtons from './ControlsButtons';
import { DeviceStatus, FLAGS, STATUS_ENUM } from '@Types';
import FormCard from './FormCard';
import { Grid } from '@mui/material';
import LocationCard from './LocationCard';
import { DeleteConfigurationDialog, ResampleSensorModal } from '@Components';
import { ConnectedHistoricalChart } from '@innovyze/summaryze';
import SensorStatusIndicator from './SensorStatusIndicator';
import { addRouteNamespace } from '@Utils/actions';
import { getValue } from '@Selectors';
import { SpinnerStatusIndicator } from './SpinnerStatusIndicator';
import { useGlobalization } from '@Translations';
import { MAIN_ROUTES } from '@Types/routes';
import { SensorConfigFormShape } from '@Types/configurations.types';
import PeekDialog from './PeekDialog';
import {
	generateConfigurationCoords,
	getSensorChartResolution,
	isVirtualSensor,
} from '@Utils';
import SensorTroubleShootDialog from './SensorTroubleShootDialog';
import { PhysicalDataSourceType } from '@Utils/constants';
import { SensorFormMode } from '@Components/SensorConfigurationForm/SensorConfigurationForm';
import { getResolutionsBySensorId } from '@Services/sensors';
import { getLocalizedResolutionLabel } from '@Utils/sensorResolution';

const EditSensor = () => {
	useNavigateTo();
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();
	const { sensorId: tempSensorId } = useParams<{ sensorId: string }>();
	const isUserAdmin = useIsAdmin();
	const [configuration, selectedConfig, validateAndGetConfChanges] =
		useConfigurationSensorDetail(tempSensorId);
	const [position, dataSources, mapChange, layerConfig] =
		useMapLocationState();
	const sensorId = configuration?.sensorId ?? tempSensorId;
	const [deviceStatus] = useSensorStatusChecker(sensorId);
	const [
		formData,
		formErrors,
		updateFormData,
		saveConfiguration,
		editedForm,
		setEditedFormState,
		peekSensorConnection,
	] = useSensorFormState(tempSensorId);
	const isVirtual = isVirtualSensor(formData.dataSourceType);
	const {
		sensorTroubleshooting,
		isOldSensorTroubleshoot,
		isCurrentlyTesting,
	} = useSensorTroubleshoot();

	const [isLiveData, setIsLiveData] = useState(true);
	const [resolutions, setResolutions] = useState<Resolutions[]>([]);
	const [selectedResolution, setSelectedResolution] = useState<Resolutions>(
		() => {
			const sourceType =
				configuration?.dataSourceType ||
				configuration?.sourceType ||
				PhysicalDataSourceType.ODBC;
			const resolution = configuration?.resolutions;

			return getSensorChartResolution(sourceType, resolution);
		},
	);
	const [showResampleModal, setShowResampleModal] = useState(false);
	const [showDeleteSensorModal, setShowDeleteSensorModal] = useState(false);
	const [showUnsavedModal, setShowUnsavedModal] = useState(false);
	const [showPeekModal, setShowPeekModal] = useState(false);
	const [showTbltModal, setShowTbltModal] = useState(false);
	const [sensorFormResampleChanges, setSensorFormResampleChanges] = useState<
		Partial<SensorFormChanges>
	>({});

	const isResampleJobsEnabled = !!useIsFeatureEnabled(FLAGS.RESAMPLE_JOBS);

	const shouldRenderTest = useMemo(
		() =>
			formData.dataSourceType === 'ODBC' ||
			formData.dataSourceType === 'OLEDB',
		[formData.dataSourceType],
	);
	const shouldRenderResample = useMemo(
		() =>
			formData?.dataSourceType === 'ODBC' ||
			formData?.dataSourceType === 'OLEDB',
		[formData.dataSourceType],
	);

	const {
		systemConnections: {
			list: systemConnectionsList,
			initialized: isSystemConnectionsInitialized,
		},
		configurations: {
			initialized: isConfigurationsInitialized,
			deletionInProgress,
			loadingConfigurationDetails,
			configurationById: { status: configurationByIdStatus },
		},
		sensorTypes: { types: sensorTypes, units },
		editSensorData: { peek: peekState },
		sensorDetails: {
			resampleJob: { status: resampleJobIssuedStatus },
		},
	} = useSelector(getValue);

	const sensorTbltingMsgs = useSensorTroubleshootMessages(
		sensorTroubleshooting,
		formData.dataSourceId,
		systemConnectionsList,
	);

	const resampleJobIssuing =
		isResampleJobsEnabled && resampleJobIssuedStatus === 'pending';

	useEffect(() => {
		if (
			sensorTroubleshooting?.state === 'FINISHED' &&
			!isCurrentlyTesting &&
			Object.keys(sensorTbltingMsgs.messages).length
		) {
			setShowTbltModal(true);
		} else {
			setShowTbltModal(false);
		}
	}, [sensorTbltingMsgs]);

	useEffect(() => {
		if (peekState.response.data.length >= 1) setShowPeekModal(true);
	}, [peekState.response.data]);

	const pageLoading = useMemo<boolean>(
		() =>
			!isConfigurationsInitialized ||
			!isSystemConnectionsInitialized ||
			loadingConfigurationDetails ||
			configurationByIdStatus === STATUS_ENUM.PENDING,
		[
			isConfigurationsInitialized,
			isSystemConnectionsInitialized,
			loadingConfigurationDetails,
			configurationByIdStatus,
		],
	);

	const sensorChartOptions = useMemo(() => {
		const baseConfig = { dislpayOptions: {}, func: 'Average' };
		return configuration
			? {
					id: configuration.sensorId ?? '',
					alias:
						configuration.sensorAlias ??
						configuration.sensorId ??
						'',
					resolution: selectedResolution,
					...baseConfig,
			  }
			: { id: '', alias: '', ...baseConfig };
	}, [configuration, isVirtual, selectedResolution]);

	useEffect(() => {
		dispatch(restoreStatusStore());
		handleDismissDialogPeek();

		return () => {
			dispatch(restoreConfigurationByIdStatus());
		};
	}, []);

	useEffect(() => {
		if (configuration) {
			const { longitude, latitude } = configuration;
			mapChange(
				stringifyLocationCoords(
					generateConfigurationCoords(longitude, latitude),
				),
			);
			dispatch(selectConfiguration({ sensorId: configuration.sensorId }));
			updateFormData(mapDataToForm(configuration));
		}
	}, [configuration]);

	useEffect(() => {
		if (selectedConfig) {
			dispatch(getConfigurationDetails(selectedConfig));
		}
	}, [selectedConfig]);

	const showResampleModalHelper = () => {
		setShowUnsavedModal(false);
		setShowResampleModal(true);
	};
	const showUnsavedModalHelper = () => {
		setShowUnsavedModal(true);
		setShowResampleModal(false);
	};
	const hideModalsHelper = () => {
		setShowUnsavedModal(false);
		setShowResampleModal(false);
	};

	const handleUpdateFormData = (
		newFormData: React.SetStateAction<SensorConfigFormShape>,
	): void => {
		updateFormData(newFormData);
		setEditedFormState(true);
	};

	const handleClickOnBackButton = () => {
		dispatch(pollSensorTroubleshootingStop());
		handleDismissDialogPeek();
		history.push(MAIN_ROUTES.SensorConfiguration);
	};

	const handleClickOnButtonSave = () => {
		setSensorFormResampleChanges({});
		if (pageLoading) {
			dispatch(error(t('Page is still loading')));
		} else if (deviceStatus !== DeviceStatus.Resample) {
			saveConfiguration();
			setEditedFormState(false);
		} else if (deviceStatus === DeviceStatus.Resample) {
			dispatch(
				error(t('Currently checking device status. Try again later')),
			);
		} else {
			dispatch(error(t('Unexpected error')));
		}
	};

	const setDeleteModalOpenStateHandler = (state: boolean) => () => {
		setShowDeleteSensorModal(state);
	};

	const handleClickOnDeleteModalButtonConfirm = () => {
		dispatch(deleteConfiguration({ sensorId: sensorId }));
		setDeleteModalOpenStateHandler(false)();
	};

	const handleClickButtonResample = () => {
		setSensorFormResampleChanges({});
		if (formData?.dataSourceType === 'OPCUA' && !editedForm) {
			dispatch(error('Sensor of type OPCUA cannot be resampled'));
			return;
		}

		const configurationChanges = validateAndGetConfChanges(formData);
		if (Object.keys(configurationChanges).length) {
			showUnsavedModalHelper();
			setSensorFormResampleChanges(configurationChanges);
		} else {
			showResampleModalHelper();
		}
	};
	const handleClickOnTestButton = () => {
		setSensorFormResampleChanges({});
		peekSensorConnection();
	};
	const handleClickButtonUnsavedCancel = () => {
		hideModalsHelper();
	};
	const handleClickButtonCloseResample = () => {
		hideModalsHelper();
	};
	const handleDismissDialogPeek = () => {
		dispatch(peekSensorReset());
		setShowPeekModal(false);
	};
	const handleDismissDialogTblt = () => {
		dispatch(pollSensorTroubleshootingStop());
		setShowTbltModal(false);
	};

	const buttonDisabledResample =
		pageLoading ||
		deviceStatus === DeviceStatus.Unknown ||
		deviceStatus === DeviceStatus.Resample ||
		deviceStatus === DeviceStatus.Unavailable ||
		deletionInProgress;

	const buttonDisabledPeek =
		pageLoading ||
		peekState.status === STATUS_ENUM.PENDING ||
		deletionInProgress;

	const buttonDisabledMultiAction = pageLoading || deletionInProgress;

	const displayInlineSpinner =
		!isOldSensorTroubleshoot &&
		!pageLoading &&
		(deletionInProgress || buttonDisabledPeek);
	const spinnerStatusText = useMemo(() => {
		if (deletionInProgress) return t('Sensor is being deleted');
		if (isCurrentlyTesting) return t('Validating Individual Fields');
		if (buttonDisabledPeek) return t('Testing Sensor');
		return '';
	}, [deletionInProgress, buttonDisabledPeek, isCurrentlyTesting]);

	useEffect(() => {
		const fetchResolutions = async () => {
			setResolutions(await getResolutionsBySensorId(sensorId));
		};

		fetchResolutions();
	}, [sensorId]);

	const MemoizedHistoricalChart = useMemo(
		() => (
			<ConnectedHistoricalChart
				data-cy="sensorGraph"
				primarySeries={{
					...sensorChartOptions,
					resolution: selectedResolution,
				}}
				isLiveData={isLiveData}
				showLiveDataButton
				onIsLiveDataChange={(isLiveData: boolean) =>
					setIsLiveData(isLiveData)
				}
				resolutionOptions={resolutions.map(res => ({
					label: getLocalizedResolutionLabel(t, res),
					value: res,
				}))}
				onResolutionChange={(resolution: Resolutions) =>
					setSelectedResolution(resolution)
				}
			/>
		),
		[sensorChartOptions, resolutions, isLiveData, selectedResolution],
	);

	if (
		configurationByIdStatus === STATUS_ENUM.ERROR ||
		(configurationByIdStatus === STATUS_ENUM.SUCCESS && !configuration)
	) {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		return <Redirect to={addRouteNamespace('sensor-configuration')} />;
	}

	const disableBtnResample =
		!isOldSensorTroubleshoot &&
		(buttonDisabledPeek || buttonDisabledResample);
	const disableBtnTest = displayInlineSpinner || disableBtnResample;
	const disableBtnMultiaction =
		!isOldSensorTroubleshoot &&
		(buttonDisabledPeek ||
			deviceStatus === DeviceStatus.Resample ||
			buttonDisabledMultiAction);

	return (
		<PageWrapperStyled
			title={sensorId}
			back={{
				action: handleClickOnBackButton,
				label: t('Back to Sensor Configuration'),
			}}
			secondary={
				isUserAdmin && (
					<StyledControlsWrapper>
						<SpinnerStatusIndicator
							visible={displayInlineSpinner}
							text={spinnerStatusText}
						/>
						<ControlsButtons
							disableResample={disableBtnResample}
							disableTest={disableBtnTest}
							disableMultiaction={disableBtnMultiaction}
							renderTest={shouldRenderTest}
							showResample={shouldRenderResample}
							showResamplingJob={resampleJobIssuing}
							disableEditSensorData={isVirtual}
							disableDeleteSensor={isVirtual}
							onClickResampleButton={handleClickButtonResample}
							onClickSaveButton={handleClickOnButtonSave}
							onClickDeleteButton={setDeleteModalOpenStateHandler(
								true,
							)}
							onClickTestConnButton={handleClickOnTestButton}
						/>
					</StyledControlsWrapper>
				)
			}
			secondaryNav={{
				items: [],
				tabSecondary: <SensorStatusIndicator status={deviceStatus} />,
			}}
			isLoading={pageLoading}
			applyPadding
			headerUnderline>
			<Grid container spacing={3}>
				<Grid item direction="column" gap={3} lg={6}>
					<FormCard
						mode={
							isUserAdmin
								? SensorFormMode.EDIT
								: SensorFormMode.VIEW
						}
						systemConnections={systemConnectionsList}
						sensorTypes={sensorTypes}
						formData={formData}
						formErrors={formErrors}
						onMapChange={mapChange}
						onUpdateFormData={handleUpdateFormData}
						units={units}
						sensorTroubleshooting={sensorTroubleshooting}
						isCurrentlyTesting={isCurrentlyTesting}
						sensorFormResampleChanges={sensorFormResampleChanges}
					/>
				</Grid>

				<Grid item lg={6}>
					<Grid container direction="column" spacing={3}>
						<Grid item>
							<LocationCard
								position={position}
								dataSources={dataSources}
								layerConfig={layerConfig}
							/>
						</Grid>
						<Grid item>
							<InfoCard title={sensorId} fullHeight>
								<ChartCard data-cy="sensorGraph">
									{sensorChartOptions.id &&
										MemoizedHistoricalChart}
								</ChartCard>
							</InfoCard>
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			<WarningDialog
				title={t('Unsaved Changes')}
				open={showUnsavedModal}
				cancelText={t('Cancel')}
				onCancel={handleClickButtonUnsavedCancel}>
				{t('The sensor contains unsaved changes.')}
			</WarningDialog>
			<ResampleSensorModal
				open={showResampleModal}
				sensorId={sensorId}
				onClose={handleClickButtonCloseResample}
				jobsEnabled={isResampleJobsEnabled}
			/>
			<DeleteConfigurationDialog
				open={showDeleteSensorModal}
				onCancel={setDeleteModalOpenStateHandler(false)}
				onConfirm={handleClickOnDeleteModalButtonConfirm}
				configurationsToDelete={[sensorId]}
			/>
			<PeekDialog
				sensorId={sensorId}
				open={showPeekModal}
				onConfirm={handleDismissDialogPeek}
				onCancel={handleDismissDialogPeek}
				data={peekState.response.data}
			/>
			<SensorTroubleShootDialog
				open={!isCurrentlyTesting && showTbltModal}
				onConfirm={handleDismissDialogTblt}
				onCancel={handleDismissDialogTblt}
				sensorTbltingMsgs={sensorTbltingMsgs}
			/>
		</PageWrapperStyled>
	);
};

export default EditSensor;
